import { SetState } from '../../context-provider';
import {
  forceSpecificCursor,
  stopForcingSpecificCursor,
} from '../../force-specific-cursor';
import {
  getMaxTransitionDurationInFrames,
  MIN_TRANSITION_DURATION_IN_FRAMES,
  TransitionItem,
} from '../../items/transition/transition-item-type';
import { EditorState } from '../../state/types';
import { getCompositionDuration } from '../../utils/get-composition-duration';
import { getVisibleFrames } from '../../utils/get-visible-frames';
import { timelineScrollContainerRef } from '../../utils/restore-scroll-after-zoom';
import { hasExceededMoveThreshold } from '../../utils/selection-utils';
import {
  calculateTransitionExtension,
  getSourceExtendableFrames,
  getTargetExtendableFrames,
} from '../../items/transition/transition-extension-utils';
import { resizeCapsuleTransition } from '../../state/actions/resize-capsule-transition';

type ExtendSide = 'left' | 'right';

interface OnTransitionExtendHandlerParams {
  pointerDownEvent: React.PointerEvent<HTMLDivElement>;
  setState: SetState;
  timelineWidth: number;
  stateAsRef: React.RefObject<EditorState>;
  transitionId: string;
  side: ExtendSide;
  height: number;
  onDragEnd: () => void;
}

export const onTransitionExtendHandler = ({
  pointerDownEvent,
  setState,
  timelineWidth,
  stateAsRef,
  transitionId,
  side,
  onDragEnd,
}: OnTransitionExtendHandlerParams) => {
  const { undoableState } = stateAsRef.current;
  const { transitions, fps, items, assets, tracks } = undoableState;

  const transition = transitions[transitionId];
  if (!transition) {
    return;
  }

  // 트랜지션 선택
  setState({
    update: (state) => ({
      ...state,
      selectedItems: [transitionId],
    }),
    commitToUndoStack: false,
  });

  // 커서 변경
  forceSpecificCursor(side === 'left' ? 'e-resize' : 'w-resize');

  const startX = pointerDownEvent.clientX;
  const initialScrollLeft = timelineScrollContainerRef.current?.scrollLeft ?? 0;

  // 초기값 저장
  const initialFrom = transition.from;
  const initialDuration = transition.durationInFrames;

  const compositionDuration = getCompositionDuration(items, tracks);
  const visibleFrames = getVisibleFrames({
    fps,
    totalDurationInFrames: compositionDuration,
  });

  let lastOffsetInFrames = 0;

  // 성능 최적화: 드래그 시작 시 확장 가능 프레임 미리 계산 (캐싱)
  const sourceItem = items[transition.sourceItemId];
  const targetItem = items[transition.targetItemId];
  const cachedSourceExtendable =
    sourceItem && targetItem
      ? getSourceExtendableFrames({ sourceItem, assets, fps })
      : 0;
  const cachedTargetExtendable =
    sourceItem && targetItem
      ? getTargetExtendableFrames({ targetItem, fps })
      : 0;

  // 최대 duration = 두 아이템 중 작은 길이
  const maxDuration =
    sourceItem && targetItem
      ? getMaxTransitionDurationInFrames(transition, sourceItem, targetItem)
      : Infinity;

  // 경계점 계산 (고정값)
  const boundaryPoint = sourceItem
    ? sourceItem.from + sourceItem.durationInFrames
    : initialFrom + initialDuration / 2;

  const updateTransition = (offsetInFrames: number, commit: boolean) => {
    // 대칭 변경: 오프셋은 양쪽에 동일하게 적용
    // 오른쪽 드래그: from 감소, duration 증가 (양쪽 확장)
    // 왼쪽 드래그: from 증가, duration 감소 (양쪽 축소)
    const effectiveOffset = side === 'right' ? offsetInFrames : -offsetInFrames;

    // 새 duration 계산 및 제한 적용
    let newDuration =
      initialDuration +
      effectiveOffset * (transition.transitionType === 'capsuleCover' ? 1 : 2);
    newDuration = Math.max(MIN_TRANSITION_DURATION_IN_FRAMES, newDuration);
    newDuration = Math.min(maxDuration, newDuration);

    // 경계점 기준으로 from 계산 (중심점 고정)
    const newFrom =
      transition.transitionType === 'capsuleCover'
        ? initialFrom
        : Math.round(boundaryPoint - newDuration / 2);

    // 확장 정보 및 frozen frames 통합 계산 (캐시된 값 사용)
    let sourceFrozenFrames = 0;
    let targetFrozenFrames = 0;
    let sourceExtendedFrames = 0;
    let targetExtendedFrames = 0;
    let targetExtendedAssetStartDelta = 0;

    if (
      transition.transitionType !== 'capsuleCover' &&
      sourceItem &&
      targetItem
    ) {
      const extension = calculateTransitionExtension({
        sourceItem,
        targetItem,
        assets,
        fps,
        transitionFrom: newFrom,
        transitionDuration: newDuration,
        cachedSourceExtendable,
        cachedTargetExtendable,
      });
      sourceExtendedFrames = extension.sourceExtendedFrames;
      targetExtendedFrames = extension.targetExtendedFrames;
      targetExtendedAssetStartDelta = extension.targetExtendedAssetStartDelta;
      sourceFrozenFrames = extension.sourceFrozenFrames;
      targetFrozenFrames = extension.targetFrozenFrames;
    }

    setState({
      update: (state) => {
        const existingTransition =
          state.undoableState.transitions[transitionId];
        if (!existingTransition) {
          return state;
        }

        if (existingTransition.transitionType === 'capsuleCover') {
          return resizeCapsuleTransition({
            state,
            transitionId,
            durationInFrames: newDuration,
          });
        }

        const updatedTransition: TransitionItem = {
          ...existingTransition,
          from: newFrom,
          durationInFrames: newDuration,
          sourceFrozenFrames,
          targetFrozenFrames,
          sourceExtendedFrames,
          targetExtendedFrames,
          targetExtendedAssetStartDelta,
        };

        return {
          ...state,
          undoableState: {
            ...state.undoableState,
            transitions: {
              ...state.undoableState.transitions,
              [transitionId]: updatedTransition,
            },
          },
        };
      },
      commitToUndoStack: commit,
    });
  };

  let hasStartedExtend = false;
  let lastPointerEvent: PointerEvent | null = null;

  const onPointerMove = (pointerMoveEvent: PointerEvent) => {
    lastPointerEvent = pointerMoveEvent;

    if (!hasStartedExtend) {
      if (!hasExceededMoveThreshold(startX, 0, pointerMoveEvent.clientX, 0)) {
        return;
      }
      hasStartedExtend = true;
    }

    // 스크롤 변경 고려
    const currentScrollLeft =
      timelineScrollContainerRef.current?.scrollLeft ?? 0;
    const scrollDelta = currentScrollLeft - initialScrollLeft;

    const offsetX = pointerMoveEvent.clientX - startX + scrollDelta;
    const offsetInFrames = Math.round(
      (offsetX / timelineWidth) * visibleFrames,
    );

    if (offsetInFrames === lastOffsetInFrames) {
      return;
    }

    lastOffsetInFrames = offsetInFrames;
    updateTransition(offsetInFrames, false);
  };

  const onScroll = () => {
    if (hasStartedExtend && lastPointerEvent) {
      const currentScrollLeft =
        timelineScrollContainerRef.current?.scrollLeft ?? 0;
      const scrollDelta = currentScrollLeft - initialScrollLeft;

      const offsetX = lastPointerEvent.clientX - startX + scrollDelta;
      const offsetInFrames = Math.round(
        (offsetX / timelineWidth) * visibleFrames,
      );

      if (offsetInFrames !== lastOffsetInFrames) {
        lastOffsetInFrames = offsetInFrames;
        updateTransition(offsetInFrames, false);
      }
    }
  };

  const onPointerUp = () => {
    stopForcingSpecificCursor();

    if (hasStartedExtend) {
      // 최종 커밋
      setState({
        update: (s) => s,
        commitToUndoStack: true,
      });
    }

    onDragEnd();

    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    timelineScrollContainerRef.current?.removeEventListener('scroll', onScroll);
  };

  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
  timelineScrollContainerRef.current?.addEventListener('scroll', onScroll);
};
